// Project Sentinel - Main Application
import { lazy, Suspense, useState, useEffect, useRef } from 'react'
import { Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom'
import { api } from './services/api'
import { whenNomosQuiet } from './services/nomos/client'
import { prefetchDefaultDashboard } from './services/dashboard-prefetch'
import { AuthProvider } from './contexts/auth-context'
import { MainLayout } from './components/layout'
import { RoutedPage } from './components/layout/PageErrorBoundary'
import { ProtectedRoute } from './components/auth/ProtectedRoute'
import LoginPage from './pages/LoginPage'
import { Skeleton } from './components/ui/skeleton'
import { LegacyTaskDetailsRedirect } from './pages/work/LegacyTaskDetailsRedirect'

// Every page is its own chunk (React.lazy): the first page renders without loading any other page's code.
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'))
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'))
const AcceptInvitationPage = lazy(() => import('./pages/AcceptInvitationPage'))
// SEN-433: the Dashboard is the Portfolio dashboard, drawn by Poiesis widgets
const DashboardHost = lazy(() => import('./pages/DashboardHost'))
const InitiativesPage = lazy(() => import('./pages/InitiativesPage'))
const BuildingManagementPage = lazy(() => import('./pages/BuildingManagementPage'))
const UsersHost = lazy(() => import('./pages/users/UsersHost'))
const ProfilePage = lazy(() => import('./pages/ProfilePage'))
const TargetsPage = lazy(() => import('./pages/TargetsPage'))
const DataPage = lazy(() => import('./pages/DataPage'))
const DataTypePage = lazy(() => import('./pages/DataTypePage'))
const DataSectionPage = lazy(() => import('./pages/DataSectionPage'))
// Connections (SEN-437): Poiesis's Connections page, hosted; the bespoke Integrations page is retired.
const ConnectionsPage = lazy(() => import('./pages/connections/ConnectionsPage'))
const MetersPage = lazy(() => import('./pages/MetersPage'))
const AssetsPage = lazy(() => import('./pages/AssetsPage'))
// Files and Skills (SEN-449/450): one page over a type's directory; Files replaces Uploads.
const LibraryPage = lazy(() => import('./pages/LibraryPage'))
// Automations is hidden (owner, 2026-10-20: no automations in use). The page code stays in pages/coo/AutomationsPage;
// the route redirects so an old bookmark lands on the dashboard instead of a blank page.
const ConsentLandingPage = lazy(() => import('./pages/ConsentLandingPage'))
const WorkPage = lazy(() => import('./pages/work/WorkPage'))
const InboxPage = lazy(() => import('./pages/InboxPage'))
const TaskPage = lazy(() => import('./pages/work/TaskPage'))
// The public field page keeps its own chunk too (technicians open only /f/:token).
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'))
const FieldViewPage = lazy(() => import('./pages/field/FieldViewPage').then((m) => ({ default: m.FieldViewPage })))

/** Shown while a page chunk loads (usually a few tens of ms on a warm cache). */
function PageFallback() {
  return (
    <div className="space-y-3 p-6" aria-busy="true">
      <Skeleton className="h-8 w-1/3" />
      <Skeleton className="h-6 w-full" />
      <Skeleton className="h-6 w-full" />
      <Skeleton className="h-64 w-full" />
    </div>
  )
}

// The chat (markdown, blocks, charts; mermaid loads on first diagram) stays out of the first load.
const ChatPage = lazy(() => import('./pages/coo/ChatPage'))
const ArtifactWindowPage = lazy(() => import('./pages/coo/ArtifactWindowPage'))
// A pinned artifact page (owner, 2026-11-03): full width, shared explicitly, the newest version.
const ArtifactPageView = lazy(() => import('./pages/coo/ArtifactPageView'))
// "What Sentinel knows" (Task 10) is its own chunk — chat and automations
// have shown a route-scoped page is worth lazy-loading, and this one is
// reachable far less often than the dashboard/buildings pages.

// Sites lived at /buildings until 28 Oct 2026; old links (emails, bookmarks) keep working.
function LegacyBuildingsRedirect() {
  const { buildingId } = useParams<{ buildingId?: string }>()
  const { search } = useLocation()
  return <Navigate to={`/sites${buildingId ? `/${buildingId}` : ''}${search}`} replace />
}

// Core Data became Data (/data, 29 Oct 2026); old links keep working, query string and all.
export function LegacyCoreDataRedirect() {
  const { '*': rest } = useParams()
  const { search } = useLocation()
  return <Navigate to={`/data${rest ? `/${rest}` : ''}${search}`} replace />
}

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(api.isAuthenticated())
  const preloadTriggeredRef = useRef(false)

  useEffect(() => {
    const authenticated = api.isAuthenticated()
    setIsAuthenticated(authenticated)

    // Strategy A: Preload dashboard data on app init if user is already
    // authenticated. This handles page refreshes and returning users with
    // valid tokens, so the dashboard paints instantly when they reach it.
    //
    // It fires on EVERY page, not just /dashboard — which is the point, but it
    // used to fire IMMEDIATELY and compete with the requests the page the user
    // actually opened needs. Measured on /initiatives: the preload's
    // dashboard-buildings call took 1820ms and was the slowest request on a
    // page that never uses it, while the page's own data waited behind it for
    // a browser connection.
    //
    // Deferring to idle keeps the benefit (the cache is warm long before
    // anyone clicks Dashboard) and removes the competition. requestIdleCallback
    // fires once the page's critical work is done; the timeout fallback covers
    // Safari, which still lacks it.
    if (authenticated && !preloadTriggeredRef.current) {
      preloadTriggeredRef.current = true
      // the dashboard warms its own questions under the keys its widgets ask (spec §6.3)
      const startWhenIdle = () => { void prefetchDefaultDashboard() }
      const ric = (window as typeof window & {
        requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number
      }).requestIdleCallback
      // requestIdleCallback alone is not enough: the main thread is idle while the page WAITS on its requests,
      // so it fired in the middle of every page load. First wait until the page's own NomOS reads have been
      // quiet for 2 s (the page is usable), then for an idle slot.
      void whenNomosQuiet(2000).then(() => {
        if (typeof ric === 'function') {
          ric(startWhenIdle, { timeout: 3000 })
        } else {
          setTimeout(startWhenIdle, 0)
        }
      })
    }
  }, [])

  const handleLogin = () => {
    setIsAuthenticated(true)
  }

  const handleLogout = () => {
    setIsAuthenticated(false)
  }

  if (!isAuthenticated) {
    return (
      <Suspense fallback={<PageFallback />}>
      <Routes>
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/accept-invitation" element={<AcceptInvitationPage />} />
        <Route path="/accept-invite" element={<AcceptInvitationPage />} />
        <Route path="/f/:token" element={<FieldViewPage />} />
        <Route path="*" element={<LoginPage onLogin={handleLogin} />} />
      </Routes>
      </Suspense>
    )
  }

  return (
    <AuthProvider>
      <MainLayout onLogout={handleLogout}>
        <RoutedPage>
        <Suspense fallback={<PageFallback />}>
        <Routes>
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/login" element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<DashboardHost />} />
          <Route path="/dashboard/:dashboardId" element={<DashboardHost />} />
          <Route path="/initiatives" element={<InitiativesPage />} />
          <Route path="/actions" element={<Navigate to="/initiatives" replace />} />
          {/* Retired pages (SEN-437, owner 2026-10-29): old links land on the page that replaced them, never a 404 */}
          <Route path="/insights" element={<Navigate to="/sites" replace />} />
          <Route path="/business-case" element={<Navigate to="/initiatives" replace />} />
          <Route path="/business-case/:id" element={<Navigate to="/initiatives" replace />} />
          <Route path="/savings-potential" element={<Navigate to="/initiatives" replace />} />
          <Route path="/proposals" element={<Navigate to="/initiatives" replace />} />
          <Route path="/reports" element={<Navigate to="/dashboard" replace />} />
          <Route path="/coo/knowledge" element={<Navigate to="/dashboard" replace />} />
          <Route path="/sites" element={<BuildingManagementPage />} />
          <Route path="/sites/:buildingId" element={<BuildingManagementPage />} />
          <Route path="/buildings" element={<LegacyBuildingsRedirect />} />
          <Route path="/buildings/:buildingId" element={<LegacyBuildingsRedirect />} />
          <Route path="/meters" element={<MetersPage />} />
          <Route path="/meters/:recordId" element={<MetersPage />} />
          <Route path="/assets" element={<AssetsPage />} />
          <Route path="/asset/:recordId" element={<AssetsPage />} />
          <Route path="/inbox" element={<InboxPage />} />
          <Route path="/work" element={<WorkPage />} />
          {/* Work (2026-10-21): a task opens in the Work page's side pane; the
              full task page (field access, links) stays one click away. */}
          <Route path="/work/tasks/:taskId" element={<WorkPage />} />
          <Route path="/work/task/:recordId" element={<TaskPage />} />
          <Route path="/work/tasks/:taskId/details" element={<LegacyTaskDetailsRedirect />} />
          {/* any record a notification is about (a mention on a site, ...) in the same pane */}
          <Route path="/work/records/:object/:recordId" element={<WorkPage />} />
          <Route path="/users" element={<ProtectedRoute minRole="admin"><UsersHost /></ProtectedRoute>} />
          <Route path="/users/:section/:recordId" element={<ProtectedRoute minRole="admin"><UsersHost /></ProtectedRoute>} />
          <Route path="/budget" element={<TargetsPage />} />
          <Route path="/budget/:recordId" element={<TargetsPage />} />
          <Route path="/core-data/*" element={<LegacyCoreDataRedirect />} />
          <Route path="/data" element={<DataPage />} />
          {/* what is not a set of records (static paths win over the :typeSlug below) */}
          <Route path="/data/spot-prices" element={<DataSectionPage section="spot-prices" />} />
          <Route path="/data/country-compliance" element={<DataSectionPage section="country-compliance" />} />
          <Route path="/data/settings" element={<DataSectionPage section="settings" />} />
          <Route path="/data/:typeSlug" element={<DataTypePage />} />
          <Route path="/data/:typeSlug/:recordId" element={<DataTypePage />} />
          <Route path="/integrations" element={<ConnectionsPage />} />
          <Route path="/consent/:connector/:requestId" element={<ConsentLandingPage />} />
          {/* Decisions retired into the Team Inbox (owner, 2026-10-23) */}
          <Route path="/decisions/*" element={<Navigate to="/inbox?scope=team" replace />} />
          {/* Files replaces Uploads (owner, 2026-10-29): the old address opens Files */}
          <Route path="/uploads" element={<Navigate to="/files" replace />} />
          <Route path="/files" element={<LibraryPage directoryKey="files" title="Files" basePath="/files"
            description="The workspace's files: uploads, chat attachments, what the agent made and artifacts" />} />
          <Route path="/files/:recordId" element={<LibraryPage directoryKey="files" title="Files" basePath="/files"
            description="The workspace's files: uploads, chat attachments, what the agent made and artifacts" />} />
          <Route path="/skills" element={<LibraryPage directoryKey="skills" title="Skills" basePath="/skills"
            description="Recipes the agent follows when a task matches: when to use each, and how" />} />
          <Route path="/skills/:recordId" element={<LibraryPage directoryKey="skills" title="Skills" basePath="/skills"
            description="Recipes the agent follows when a task matches: when to use each, and how" />} />
          <Route path="/coo/automations" element={<Navigate to="/dashboard" replace />} />
          <Route path="/automations" element={<Navigate to="/dashboard" replace />} />
          <Route path="/coo/chat/:threadId?" element={<Suspense fallback={<Skeleton className="h-[calc(100dvh-3rem)] w-full" />}><ChatPage /></Suspense>} />
          <Route path="/coo/artifact/:threadId" element={<Suspense fallback={<Skeleton className="h-[calc(100dvh-3rem)] w-full" />}><ArtifactWindowPage /></Suspense>} />
          <Route path="/pages/:pageId" element={<ArtifactPageView />} />
          <Route path="/settings" element={<ProfilePage />} />
          <Route path="/accept-invitation" element={<AcceptInvitationPage />} />
          <Route path="/accept-invite" element={<AcceptInvitationPage />} />
          {/* any other address: say so, never an empty work area */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        </Suspense>
        </RoutedPage>
      </MainLayout>
    </AuthProvider>
  )
}

export default App
